import { observable, runInAction } from 'mobx';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';
import LanguageStore from './LanguageStore';
import RootStore from './RootStore';

const THIRTY_SECONDS = 30 * 1000;

interface Category {
  id: number;
  name: string;
  link: string;
}

export interface DigitarkNewsItem {
  id: string;
  date: string;
  originalTitle: string;
  feedImageUrl?: string;
  excerpt?: string;
  link?: string;
  categories: Category[];
}

export interface NewsHolder {
  items: DigitarkNewsItem[];
  status: string;
  total?: number;
}

class DigitarkNewsStore {
  @observable accessor news = new Map<string, NewsHolder>();

  private languageStore: LanguageStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
  }

  public getNewsItems(count: number = 2, tag: string = 'default', video?: boolean, featuredTeliaHome: boolean = false) {
    return this.getNewsHolder(tag, video, featuredTeliaHome).items.slice(0, count);
  }

  public loadNews(amount: number = 2, tag: string = 'default', video?: boolean, featuredTeliaHome: boolean = false) {
    if (this.fetchNotRequired(amount, tag, video, featuredTeliaHome)) {
      return Promise.resolve();
    }
    runInAction(() => {
      this.getNewsHolder(tag, video, featuredTeliaHome).status = 'FETCHING';
    });
    return this.fetchNews(amount, tag, video, featuredTeliaHome);
  }

  private fetchNews(amount: number = 2, tag: string = 'default', video?: boolean, featuredTeliaHome: boolean = false) {
    const lang = this.languageStore.currentLanguage;
    const params: KeyValue<any> = {
      context: 'embed',
      perPage: amount,
      featuredTeliaHome,
      tags: tag === 'default' ? [] : [tag]
    };
    if (video !== undefined) {
      params.video = video ? '1' : '0';
    }
    return API.get(`/digitark/news/${lang}`, {
      params,
      timeout: THIRTY_SECONDS
    })
      .then((response) => {
        this.addNewsToMap(response.data, tag, video, featuredTeliaHome);
      })
      .catch((e) => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e);
        runInAction(() => {
          this.getNewsHolder(tag, video, featuredTeliaHome).status = 'NOT_FOUND';
        });
      });
  }

  public notFound(tag: string = 'default', video?: boolean, featuredTeliaHome: boolean = false) {
    return this.getNewsHolder(tag, video, featuredTeliaHome).status === 'NOT_FOUND';
  }

  public isFetching(tag: string = 'default', video?: boolean, featuredTeliaHome: boolean = false) {
    return ['UNINITIALIZED', 'FETCHING'].includes(this.getNewsHolder(tag, video, featuredTeliaHome).status);
  }

  private fetchNotRequired(amount: number, tag: string, video: boolean, featuredTeliaHome: boolean) {
    const newsHolder = this.getNewsHolder(tag, video, featuredTeliaHome);
    return newsHolder.items.length >= amount || (newsHolder.total && newsHolder.total < amount);
  }

  private addNewsToMap(data: { items: DigitarkNewsItem[]; total: number }, tag: string, video: boolean, featuredTeliaHome: boolean) {
    const newsHolder = this.getNewsHolder(tag, video, featuredTeliaHome);
    runInAction(() => {
      newsHolder.status = 'FETCHED';
      newsHolder.items = data.items;
      newsHolder.total = data.total;
    });
  }

  private getNewsHolder(tag: string, video: boolean, featuredTeliaHome: boolean) {
    const key = this.buildKey(tag, video, featuredTeliaHome);
    if (!this.news.get(key)) {
      runInAction(() => {
        this.news.set(key, { items: [], status: 'UNINITIALIZED' });
      });
    }
    return this.news.get(key);
  }

  private buildKey(tag: string = 'default', video?: boolean, featuredTeliaHome: boolean = false) {
    const lang = this.languageStore.currentLanguage;
    return `${tag}_${lang}${video !== undefined ? (video ? '_video_1' : '_video_0') : ''}${featuredTeliaHome ? '_featuredTeliaHome' : ''}`;
  }
}

export default DigitarkNewsStore;
